import { useEffect, useState } from "react";
import { Copy, X } from "lucide-react";

// 엑셀식 표 조작(2026-07-18 피드백) — 대시보드의 모든 표에 전역으로 적용한다
// (표가 수십 곳이라 컴포넌트마다 붙이지 않는다 — 셀 더블클릭 복사와 같은 원칙).
//
//  · 가볍게 긁기(드래그): 엑셀처럼 셀 범위가 선택되고, 하단 바에 선택 칸 수·합계·평균이 뜬다
//  · 더블클릭 후 잡은 채 끌기: 표가 좌우로 끌려 이동한다(가로 스크롤 팬) — 처음엔
//    꾹(0.4초) 누르기였는데 더블클릭-드래그가 더 편하다는 피드백(2026-07-18)으로 교체
//  · 빈 곳 클릭 또는 Esc: 선택 해제
//
// 셀 더블클릭 복사(CellCopyToast)와 공존 — 이동 없이 빠르게 더블클릭하면 복사, 두 번째
// 클릭을 잡은 채 끌면 이동(끌었을 때는 dblclick 을 캡처 단계에서 삼켜 복사가 안 나가게).
// 행 클릭 동작(사용자 표의 상세 열기)과의 충돌은, 드래그 선택이 끝난 직후의 click 이벤트
// 한 번을 캡처 단계에서 삼켜서 막는다.

// "₩305,400" · "$77.0069" · "71.3%" · "1,234 크레딧" 같은 표시 문자열에서 숫자만 추출.
function parseNum(text) {
  const cleaned = String(text).replace(/[₩$,%\s]|크레딧|건|명|회/g, "");
  const m = cleaned.match(/^-?\d+(\.\d+)?$/);
  return m ? parseFloat(m[0]) : null;
}

function fmtNum(n) {
  return Number(n.toFixed(4)).toLocaleString("ko-KR");
}

export default function TableInteract() {
  // stats: { count, numCount, sum, avg, tsv } — 드래그 선택이 끝나면 채워진다.
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let start = null;      // { table, r, c }
    let mode = null;       // "maybe"(1클릭, 끌면 선택) | "panReady"(2클릭째, 끌면 이동) | "select" | "pan"
    let startXY = null;
    let panEl = null;      // 가로 스크롤 컨테이너(overflow-x-auto)
    let selCells = [];
    let suppressClick = false;
    let suppressDblClick = false;
    let lastUp = { time: 0, x: 0, y: 0 }; // 더블클릭(두 번째 mousedown) 판정용

    const cellPos = (td) => {
      const tr = td.parentElement;
      const table = td.closest("table");
      return { table, r: [...table.rows].indexOf(tr), c: [...tr.cells].indexOf(td) };
    };

    function clearSel() {
      selCells.forEach((el) => el.classList.remove("cell-sel"));
      selCells = [];
    }

    function applySel(a, b) {
      clearSel();
      const rows = [...a.table.rows];
      const [r1, r2] = [Math.min(a.r, b.r), Math.max(a.r, b.r)];
      const [c1, c2] = [Math.min(a.c, b.c), Math.max(a.c, b.c)];
      for (let r = r1; r <= r2; r++) {
        const cells = rows[r]?.cells;
        if (!cells) continue;
        for (let c = c1; c <= c2; c++) {
          if (cells[c]) {
            cells[c].classList.add("cell-sel");
            selCells.push(cells[c]);
          }
        }
      }
    }

    function finishSelection() {
      const nums = selCells.map((el) => parseNum(el.innerText)).filter((v) => v != null && Number.isFinite(v));
      const sum = nums.reduce((s, v) => s + v, 0);
      // 선택 범위를 행 단위 TSV로 — 통계 바의 "복사"로 노션/시트에 그대로 붙일 수 있게.
      const byRow = new Map();
      selCells.forEach((el) => {
        const r = el.parentElement;
        if (!byRow.has(r)) byRow.set(r, []);
        byRow.get(r).push(el.innerText.trim().replace(/[\t\r\n]+/g, " "));
      });
      const tsv = [...byRow.values()].map((cells) => cells.join("\t")).join("\n");
      setStats({
        count: selCells.length,
        numCount: nums.length,
        sum,
        avg: nums.length ? sum / nums.length : null,
        tsv,
      });
    }

    function onDown(e) {
      if (e.button !== 0) return;
      // 셀 안의 입력창·버튼(퀵 조정 팝오버 등)에서는 제스처를 시작하지 않는다 — 입력창
      // 텍스트 드래그가 셀 범위 선택으로 오인되는 것 방지.
      if (e.target.closest("input, textarea, select, button, a")) return;
      const td = e.target.closest("td, th");
      if (!td || !td.closest("table")) {
        // 표 밖 클릭 — 기존 선택·통계 해제 (통계 바 안의 버튼 클릭은 예외)
        if (!e.target.closest("[data-table-stats]")) {
          clearSel();
          setStats(null);
        }
        return;
      }
      start = cellPos(td);
      startXY = { x: e.clientX, y: e.clientY };
      panEl = td.closest(".overflow-x-auto");
      // 직전 클릭에서 400ms·10px 안에 다시 눌렀으면 더블클릭의 두 번째 클릭 — 잡은 채
      // 끌면 표 이동. 이동 없이 그대로 떼면 브라우저 dblclick 이 발화해 셀 복사가 된다.
      const isSecondClick =
        Date.now() - lastUp.time < 400 &&
        Math.hypot(e.clientX - lastUp.x, e.clientY - lastUp.y) < 10;
      mode = isSecondClick ? "panReady" : "maybe";
      // 두 번째 클릭을 잡은 순간부터 손바닥 커서 — "이제 끌면 이동"을 커서로 예고.
      if (isSecondClick) document.body.classList.add("table-grab");
    }

    function onMove(e) {
      if (!start) return;
      const moved = Math.hypot(e.clientX - startXY.x, e.clientY - startXY.y) > 5;
      if (mode === "maybe" && moved) {
        mode = "select";
        document.body.classList.add("table-dragging");
      } else if (mode === "panReady" && moved) {
        mode = "pan";
        document.body.classList.add("table-dragging");
        document.body.classList.replace("table-grab", "table-grabbing");
      }
      if (mode === "select") {
        const td = document.elementFromPoint(e.clientX, e.clientY)?.closest("td, th");
        if (td && td.closest("table") === start.table) applySel(start, cellPos(td));
      } else if (mode === "pan") {
        if (panEl) panEl.scrollLeft -= e.movementX;
        window.scrollBy(0, -e.movementY);
      }
    }

    function onUp(e) {
      if (mode === "select" && selCells.length > 1) {
        suppressClick = true; // 드래그 직후의 click 한 번은 행 클릭 동작으로 새지 않게 삼킨다
        finishSelection();
      } else if (mode === "pan") {
        // 끌어서 이동했으면 이어지는 dblclick(셀 복사)·click(행 상세)은 삼킨다
        suppressClick = true;
        suppressDblClick = true;
      } else if (mode === "maybe") {
        clearSel();
        setStats(null);
      }
      if (start) lastUp = { time: Date.now(), x: e.clientX, y: e.clientY };
      document.body.classList.remove("table-dragging", "table-grab", "table-grabbing");
      start = null;
      mode = null;
    }

    function onClickCapture(e) {
      if (suppressClick) {
        e.stopPropagation();
        e.preventDefault();
        suppressClick = false;
      }
    }

    function onDblClickCapture(e) {
      if (suppressDblClick) {
        e.stopPropagation();
        e.preventDefault();
        suppressDblClick = false;
      }
    }

    function onKeyDown(e) {
      if (e.key === "Escape") {
        clearSel();
        setStats(null);
      }
    }

    document.addEventListener("mousedown", onDown);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("click", onClickCapture, true);
    document.addEventListener("dblclick", onDblClickCapture, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("click", onClickCapture, true);
      document.removeEventListener("dblclick", onDblClickCapture, true);
      document.removeEventListener("keydown", onKeyDown);
      clearSel();
    };
  }, []);

  if (!stats) return null;
  return (
    <div
      data-table-stats
      className="fixed bottom-6 left-1/2 z-50 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-full bg-ink-900 py-2 pl-4 pr-2 text-xs font-medium text-white shadow-lg"
    >
      <span className="tabular-nums">{stats.count}칸 선택</span>
      {stats.numCount > 0 && (
        <>
          <span className="tabular-nums">합계 {fmtNum(stats.sum)}</span>
          <span className="tabular-nums">평균 {fmtNum(stats.avg)}</span>
        </>
      )}
      <button
        type="button"
        onClick={() => navigator.clipboard.writeText(stats.tsv).catch(() => {})}
        title="선택한 범위를 복사 — 노션·시트에 표로 붙여넣기"
        className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 hover:bg-white/25"
      >
        <Copy className="h-3 w-3" />
        복사
      </button>
      <button
        type="button"
        onClick={() => {
          document.querySelectorAll(".cell-sel").forEach((el) => el.classList.remove("cell-sel"));
          setStats(null);
        }}
        aria-label="선택 해제"
        className="rounded-full p-1 hover:bg-white/15"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
